import { useState, useEffect, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const appearance = {
  theme: "flat",
  variables: {
    colorPrimary: "#B76E79",
    colorBackground: "#FFFFFF",
    colorText: "#0A0A0A",
    colorTextSecondary: "#6f6a63",
    colorDanger: "#b3261e",
    fontFamily: "Manrope, sans-serif",
    fontSizeBase: "14px",
    borderRadius: "2px",
  },
  rules: {
    ".Input": { border: "1px solid #E3DDD3", backgroundColor: "#FFFFFF", padding: "12px" },
    ".Input:focus": { border: "1px solid #B76E79", boxShadow: "none", outline: "none" },
    ".Label": { fontFamily: "Manrope, sans-serif", fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.18em", color: "#8a847c", marginBottom: "6px" },
    ".Tab": { border: "1px solid #E3DDD3" },
    ".Tab--selected": { border: "1px solid #B76E79" },
  },
};
const fonts = [{ cssSrc: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600&display=swap" }];

function InnerForm({ intentId, cta, onSuccess, onBack, backLabel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: window.location.href },
    });
    if (error) {
      toast.error(error.message || "Payment failed — please check your card.");
      setSubmitting(false);
      return;
    }
    try {
      for (let i = 0; i < 6; i++) {
        const { data } = await api.intentStatus(intentId);
        if (data.payment_status === "paid") { onSuccess(data); return; }
        await new Promise((r) => setTimeout(r, 1500));
      }
      toast("Payment is processing — we'll confirm it shortly.");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="payment-element-form">
      <PaymentElement onReady={() => setReady(true)} options={{ layout: "tabs" }} />
      {!ready && <div className="flex items-center justify-center py-6 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {ready && (
        <>
          <button data-testid="payment-submit-btn" onClick={pay} disabled={submitting} className="w-full bg-ink text-ivory py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Securing your look…</> : <><Lock className="w-4 h-4" /> {cta}</>}
          </button>
          {onBack && (
            <button onClick={onBack} disabled={submitting} className="w-full font-label text-[9px] text-muted-foreground flex items-center justify-center gap-1.5 disabled:opacity-40">
              <ArrowLeft className="w-3 h-3" /> {backLabel || "Back"}
            </button>
          )}
          <p className="font-label text-[8px] text-muted-foreground flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Encrypted & secured by Stripe
          </p>
        </>
      )}
    </div>
  );
}

export function PaymentElementForm({ payload, cta, onSuccess, onBack, backLabel, onSchoolVerifyRequired }) {
  const [secret, setSecret] = useState(null);
  const [intentId, setIntentId] = useState(null);
  const created = useRef(false);

  useEffect(() => {
    if (created.current) return;
    created.current = true;
    api.checkoutIntent(payload)
      .then(({ data }) => { setSecret(data.client_secret); setIntentId(data.intent_id); })
      .catch((e) => {
        const code = e?.response?.data?.detail?.code;
        if (e?.response?.status === 403 && code === "school_not_verified") {
          onSchoolVerifyRequired && onSchoolVerifyRequired();
          onBack && onBack();
          return;
        }
        toast.error(errMsg(e));
        onBack && onBack();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stripePromise || !secret) {
    return <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: secret, appearance, fonts }}>
      <InnerForm intentId={intentId} cta={cta} onSuccess={onSuccess} onBack={onBack} backLabel={backLabel} />
    </Elements>
  );
}
